import axios from "axios";

class ApiClient {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 1200000, // 20 minutes for long-running AI operations like holistic generation
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getStoredToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        return response.data;
      },
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // Try to refresh token
          try {
            const refreshToken = localStorage.getItem("refreshToken");
            if (refreshToken) {
              const response = await axios.post(
                `${this.baseURL}/api/auth/refresh`,
                {
                  refreshToken,
                }
              );

              if (response.data.success) {
                const { token } = response.data.data;
                localStorage.setItem("authToken", token);
                this.setAuthToken(token);

                // Retry original request
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.client(originalRequest);
              }
            }
          } catch (refreshError) {
            console.error("Token refresh failed:", refreshError);
          }

          // If refresh fails, redirect to login
          this.handleAuthError();
        }

        return Promise.reject(error);
      }
    );
  }

  getStoredToken() {
    return localStorage.getItem("authToken");
  }

  setAuthToken(token) {
    if (token) {
      this.client.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete this.client.defaults.headers.common["Authorization"];
    }
  }

  handleAuthError() {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    this.setAuthToken(null);

    // Only redirect if we're not already on the login page
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  // HTTP Methods
  async get(url, config = {}) {
    return this.client.get(url, config);
  }

  async post(url, data = {}, config = {}) {
    return this.client.post(url, data, config);
  }

  async put(url, data = {}, config = {}) {
    return this.client.put(url, data, config);
  }

  async patch(url, data = {}, config = {}) {
    return this.client.patch(url, data, config);
  }

  async delete(url, config = {}) {
    return this.client.delete(url, config);
  }

  // File upload method
  async uploadFiles(url, files, additionalData = {}, onProgress = null) {
    const formData = new FormData();

    // Add files
    if (Array.isArray(files)) {
      files.forEach((file) => {
        formData.append("documents", file);
      });
    } else {
      formData.append("documents", files);
    }

    // Add additional data
    Object.keys(additionalData).forEach((key) => {
      formData.append(key, additionalData[key]);
    });

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    return this.client.post(url, formData, config);
  }

  // Health check
  async checkHealth() {
    try {
      const response = await this.get("/api/health");
      return response;
    } catch (error) {
      console.error("Health check failed:", error);
      return { status: "unhealthy", error: error.message };
    }
  }

  // Auth-specific methods
  async login(credentials) {
    return this.post("/api/auth/login", credentials);
  }

  async logout() {
    return this.post("/api/auth/logout");
  }

  async getCurrentUser() {
    return this.get("/api/auth/me");
  }

  async register(userData) {
    return this.post("/api/auth/register", userData);
  }

  // Proposal methods
  async getProposals(params = {}) {
    return this.get("/api/proposals", { params });
  }

  async getProposal(id) {
    return this.get(`/api/proposals/${id}`);
  }

  async createProposal(proposalData) {
    return this.post("/api/proposals", proposalData);
  }

  async updateProposal(id, proposalData) {
    return this.put(`/api/proposals/${id}`, proposalData);
  }

  async deleteProposal(id) {
    return this.delete(`/api/proposals/${id}`);
  }

  async updateProposalStatus(id, status) {
    return this.patch(`/api/proposals/${id}/status`, { status });
  }

  // Customer methods (NEW)
  async getCustomers(params = {}) {
    return this.get("/api/customers", { params });
  }

  async getCustomer(id) {
    return this.get(`/api/customers/${id}`);
  }

  async createCustomer(customerData) {
    return this.post("/api/customers", customerData);
  }

  async updateCustomer(id, customerData) {
    return this.put(`/api/customers/${id}`, customerData);
  }

  async deleteCustomer(id) {
    return this.delete(`/api/customers/${id}`);
  }

  // Document methods
  async getDocumentTypes() {
    return this.get("/api/documents/types");
  }

  async getProposalDocuments(proposalId) {
    return this.get(`/api/documents/proposal/${proposalId}`);
  }

  async uploadDocuments(
    proposalId,
    files,
    documentTypeId = null,
    onProgress = null
  ) {
    const additionalData = { proposalId };
    if (documentTypeId) {
      additionalData.documentTypeId = documentTypeId;
    }

    return this.uploadFiles(
      "/api/documents/upload",
      files,
      additionalData,
      onProgress
    );
  }

  async deleteDocument(documentId) {
    return this.delete(`/api/documents/${documentId}`);
  }

  // Chat methods
  async getChatSessions(proposalId = null) {
    const params = proposalId ? { proposalId } : {};
    return this.get("/api/chat/sessions", { params });
  }

  async createChatSession(sessionName, proposalId = null) {
    const requestBody = {};
    if (sessionName) {
      requestBody.sessionName = sessionName;
    }
    if (proposalId) {
      requestBody.proposalId = proposalId;
    }
    return this.post("/api/chat/sessions", requestBody);
  }

  async getChatSession(sessionId) {
    return this.get(`/api/chat/sessions/${sessionId}`);
  }

  async sendMessage(sessionId, message, options = {}) {
    return this.post(`/api/chat/sessions/${sessionId}/messages`, {
      message,
      options, // This now includes referencedDocuments AND chatDocuments
    });
  }

  async updateChatSession(sessionId, updates) {
    return this.put(`/api/chat/sessions/${sessionId}`, updates);
  }

  async deleteChatSession(sessionId) {
    return this.delete(`/api/chat/sessions/${sessionId}`);
  }

  async searchMessages(query, proposalId = null) {
    const params = { q: query };
    if (proposalId) params.proposalId = proposalId;
    return this.get("/api/chat/search", { params });
  }

  // NEW: Chat Documents methods
  /**
   * Upload documents to a specific chat session
   * @param {number} sessionId - The chat session ID
   * @param {File[]} files - Array of files to upload
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise} API response
   */
  async uploadChatDocuments(sessionId, files, onProgress = null) {
    const formData = new FormData();

    // Add files to FormData
    if (Array.isArray(files)) {
      files.forEach((file) => {
        formData.append("documents", file);
      });
    } else {
      formData.append("documents", files);
    }

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    return this.client.post(
      `/api/chat/sessions/${sessionId}/documents`,
      formData,
      config
    );
  }

  /**
   * Get documents for a specific chat session
   * @param {number} sessionId - The chat session ID
   * @returns {Promise} API response with documents
   */
  async getChatDocuments(sessionId) {
    return this.get(`/api/chat/sessions/${sessionId}/documents`);
  }

  /**
   * Remove a document from a chat session
   * @param {number} sessionId - The chat session ID
   * @param {number} documentId - The chat document ID
   * @returns {Promise} API response
   */
  async removeChatDocument(sessionId, documentId) {
    return this.delete(
      `/api/chat/sessions/${sessionId}/documents/${documentId}`
    );
  }

  /**
   * Get a specific chat document
   * @param {number} sessionId - The chat session ID
   * @param {number} documentId - The chat document ID
   * @returns {Promise} API response with document details
   */
  async getChatDocument(sessionId, documentId) {
    return this.get(`/api/chat/sessions/${sessionId}/documents/${documentId}`);
  }

  // AI methods
  async queryAI(message, proposalId = null, options = {}) {
    return this.post("/api/ai/query", {
      message,
      proposalId,
      options,
    });
  }

  async getAIProviders() {
    return this.get("/api/ai/providers");
  }

  async getAIModels() {
    return this.get("/api/ai/models");
  }

  async setDefaultAIModel(model) {
    return this.put("/api/ai/default-model", { model });
  }

  async searchVectorDatabase(query, proposalId = null, limit = 5) {
    return this.post("/api/ai/vector/search", {
      query,
      proposalId,
      limit,
    });
  }

  // Admin methods
  async getUsers(params = {}) {
    return this.get("/api/admin/users", { params });
  }

  async createUser(userData) {
    return this.post("/api/admin/users", userData);
  }

  async updateUser(userId, userData) {
    return this.put(`/api/admin/users/${userId}`, userData);
  }

  async resetUserPassword(userId, newPassword) {
    return this.put(`/api/admin/users/${userId}/reset-password`, { newPassword });
  }

  async deleteUser(userId) {
    return this.delete(`/api/admin/users/${userId}`);
  }

  async getSystemStatus() {
    return this.get("/api/admin/system-status");
  }

  async reprocessDocuments() {
    return this.post("/api/admin/reprocess-documents");
  }

  async getAuditLogs(params = {}) {
    return this.get("/api/admin/audit-logs", { params });
  }

  // Document viewing/download methods
  getDocumentViewUrl(documentId) {
    return `${this.baseURL}/api/documents/${documentId}/view`;
  }

  getDocumentDownloadUrl(documentId) {
    return `${this.baseURL}/api/documents/${documentId}/download`;
  }

  async viewDocument(documentId) {
    // Get the current auth token
    const token = this.getStoredToken();
    const viewUrl = this.getDocumentViewUrl(documentId);

    if (token) {
      // Add token as query parameter
      const urlWithToken = `${viewUrl}?token=${encodeURIComponent(token)}`;
      window.open(urlWithToken, "_blank");
    } else {
      // No token available
      console.error("No authentication token available");
      window.open(viewUrl, "_blank");
    }
  }

  async downloadDocument(documentId) {
    // This will force download the document
    window.open(this.getDocumentDownloadUrl(documentId), "_blank");
  }

  // Predefined Prompts methods
  async getPredefinedPrompts(category = null, limit = 50) {
    const params = { limit };
    if (category) params.category = category;
    return this.get("/api/predefined-prompts", { params });
  }

  async getPromptCategories() {
    return this.get("/api/predefined-prompts/categories");
  }

  async getPredefinedPrompt(promptId) {
    return this.get(`/api/predefined-prompts/${promptId}`);
  }

  async createPredefinedPrompt(promptData) {
    return this.post("/api/predefined-prompts", promptData);
  }

  async updatePredefinedPrompt(promptId, updates) {
    return this.put(`/api/predefined-prompts/${promptId}`, updates);
  }

  async deletePredefinedPrompt(promptId) {
    return this.delete(`/api/predefined-prompts/${promptId}`);
  }

  async updateDocumentType(documentId, documentTypeId) {
    return this.put(`/api/documents/${documentId}/type`, {
      documentTypeId: documentTypeId,
    });
  }

  // Add these methods to your existing apiClient.js file

  // Editable Documents methods
  async getDocumentTemplates() {
    return this.get("/api/editable-documents/templates");
  }

  async getEditableDocuments(proposalId) {
    return this.get(`/api/editable-documents/proposal/${proposalId}`);
  }

  async getEditableDocument(documentId) {
    return this.get(`/api/editable-documents/${documentId}`);
  }

  async createEditableDocument(
    proposalId,
    templateId,
    documentName,
    options = {}
  ) {
    const requestData = {
      proposalId,
      templateId,
      documentName,
      generateInitialContent: options.generateInitialContent || false,
      sourceDocuments: options.sourceDocuments || [],
    };

    return this.post("/api/editable-documents", requestData);
  }

  async updateEditableDocument(documentId, updates) {
    return this.put(`/api/editable-documents/${documentId}`, updates);
  }

  async deleteEditableDocument(documentId) {
    return this.delete(`/api/editable-documents/${documentId}`);
  }

  // Section management methods
  async addDocumentSection(
    documentId,
    sectionTitle,
    insertPosition,
    content = ""
  ) {
    return this.post(`/api/editable-documents/${documentId}/sections`, {
      sectionTitle,
      insertPosition,
      content,
    });
  }

  async updateDocumentSection(sectionId, updates) {
    return this.put(`/api/editable-documents/sections/${sectionId}`, updates);
  }

  async deleteDocumentSection(sectionId) {
    return this.delete(`/api/editable-documents/sections/${sectionId}`);
  }

  async reorderDocumentSections(documentId, sectionOrderMap) {
    return this.put(`/api/editable-documents/${documentId}/sections/reorder`, {
      sectionOrderMap,
    });
  }

  // ENHANCED: AI generation methods
  /**
   * Generate AI content for a document section
   * @param {number} sectionId - The section ID
   * @param {string} operation - The operation type (generate, expand, shorten, bulletize, custom)
   * @param {object} options - Additional options (customPrompt, sourceDocuments, additionalInstructions)
   * @returns {Promise} API response
   */
  async generateSectionContent(sectionId, operation, options = {}) {
    try {
      console.log(
        `🔥 API: Generating ${operation} for section ${sectionId}`,
        options
      );

      const response = await this.post(
        `/api/editable-documents/sections/${sectionId}/generate`,
        {
          operation,
          customPrompt: options.customPrompt || "",
          sourceDocuments: options.sourceDocuments || [],
          additionalInstructions: options.additionalInstructions || "",
          contextDocumentId: options.contextDocumentId || null,
        }
      );

      console.log("🔥 API Response:", response);
      return response;
    } catch (error) {
      console.error("❌ API Error in generateSectionContent:", error);
      throw error;
    }
  }

  // NEW: Generate entire document from RFP
  async generateDocumentFromRFP(
    proposalId,
    documentType,
    templateId,
    documentName,
    options = {}
  ) {
    return this.post("/api/editable-documents/generate-from-rfp", {
      proposalId,
      documentType,
      templateId,
      documentName,
      additionalInstructions: options.additionalInstructions,
    });
  }

  // NEW: Get AI generation statistics
  async getAIGenerationStats(days = 30, global = false) {
    return this.get("/api/editable-documents/ai/stats", {
      params: { days, global },
    });
  }

  // Section enhancement methods
  async analyzeMissingSections(documentId, selectedModel) {
    return this.post(`/api/editable-documents/${documentId}/analyze-missing-sections`, {
      selectedModel
    });
  }

  async generateSection(documentId, sectionSpec) {
    return this.post(`/api/editable-documents/${documentId}/generate-section`, sectionSpec);
  }

  // Document duplication
  async duplicateDocument(documentId) {
    return this.post(`/api/editable-documents/${documentId}/duplicate`);
  }

  // Document rename
  async renameDocument(documentId, documentName) {
    return this.patch(`/api/editable-documents/${documentId}/rename`, {
      documentName
    });
  }

  // Export methods
  async exportEditableDocument(documentId) {
    try {
      const token = this.getStoredToken();
      const response = await fetch(
        `${this.baseURL}/api/editable-documents/${documentId}/export`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export document');
      }

      // Get the blob
      const blob = await response.blob();

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'document.docx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true };
    } catch (error) {
      console.error('Export document error:', error);
      throw error;
    }
  }

  // Template Management methods
  async getTemplates(activeOnly = false) {
    try {
      const params = activeOnly ? { active_only: "true" } : {};
      return this.get("/api/templates", { params });
    } catch (error) {
      console.error("getTemplates error:", error);
      throw error;
    }
  }

  async getTemplate(templateId) {
    try {
      return this.get(`/api/templates/${templateId}`);
    } catch (error) {
      console.error("getTemplate error:", error);
      throw error;
    }
  }

  async createTemplate(templateData) {
    try {
      return this.post("/api/templates", templateData);
    } catch (error) {
      console.error("createTemplate error:", error);
      throw error;
    }
  }

  async updateTemplate(templateId, updates) {
    try {
      return this.put(`/api/templates/${templateId}`, updates);
    } catch (error) {
      console.error("updateTemplate error:", error);
      throw error;
    }
  }

  async deleteTemplate(templateId) {
    try {
      return this.delete(`/api/templates/${templateId}`);
    } catch (error) {
      console.error("deleteTemplate error:", error);
      throw error;
    }
  }

  async duplicateTemplate(templateId, newName) {
    try {
      return this.post(`/api/templates/${templateId}/duplicate`, {
        new_name: newName,
      });
    } catch (error) {
      console.error("duplicateTemplate error:", error);
      throw error;
    }
  }

  async getTemplateTypes() {
    try {
      return this.get("/api/templates/types");
    } catch (error) {
      console.error("getTemplateTypes error:", error);
      throw error;
    }
  }
  // Get user's proposal access
  async getUserProposalAccess(userId) {
    return this.get(`/api/admin/users/${userId}/proposals`);
  }

  // Update user's proposal access
  async updateUserProposalAccess(userId, proposalIds) {
    return this.put(`/api/admin/users/${userId}/proposals`, {
      proposalIds: proposalIds,
    });
  }

  // Get proposal's user access
  async getProposalUserAccess(proposalId) {
    return this.get(`/api/proposals/${proposalId}/users`);
  }

  // Update proposal's user access
  async updateProposalUserAccess(proposalId, userIds) {
    return this.put(`/api/proposals/${proposalId}/users`, {
      userIds: userIds,
    });
  }

  // Section Chat methods
  async createSectionChatSession(sectionId, documentId, sessionName = null) {
    return this.post(`/api/sections/${sectionId}/chat/sessions`, {
      documentId,
      sessionName,
    });
  }

  async getCurrentSectionChatSession(sectionId, documentId) {
    return this.get(`/api/sections/${sectionId}/chat/sessions/current`, {
      params: { documentId }
    });
  }

  async getSectionChatSession(sectionId, sessionId) {
    return this.get(`/api/sections/${sectionId}/chat/sessions/${sessionId}`);
  }

  async sendSectionChatMessage(sectionId, sessionId, message, currentSectionContent, referencedDocuments = [], selectedModel = null, companyDocuments = {}) {
    return this.post(`/api/sections/${sectionId}/chat/sessions/${sessionId}/messages`, {
      message,
      currentSectionContent,
      referencedDocuments,
      selectedModel,
      referencedPastPerformance: companyDocuments.pastPerformanceIds || [],
      referencedResumes: companyDocuments.resumeIds || [],
      referencedModelContent: companyDocuments.modelContentIds || [],
    });
  }

  async uploadSectionChatDocument(sectionId, sessionId, file, onProgress = null) {
    const formData = new FormData();
    formData.append('document', file);

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    if (onProgress) {
      config.onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      };
    }

    return this.client.post(
      `/api/sections/${sectionId}/chat/sessions/${sessionId}/documents`,
      formData,
      config
    );
  }

  async getSectionChatDocuments(sectionId, sessionId) {
    return this.get(`/api/sections/${sectionId}/chat/sessions/${sessionId}/documents`);
  }

  async removeSectionChatDocument(sectionId, sessionId, documentId) {
    return this.delete(`/api/sections/${sectionId}/chat/sessions/${sessionId}/documents/${documentId}`);
  }

  async getProposalDocumentsForSection(sectionId, documentId) {
    return this.get(`/api/sections/${sectionId}/chat/documents/proposal`, {
      params: { documentId }
    });
  }

  async acceptSectionContent(sectionId, content, messageId = null, changeSummary = null) {
    return this.post(`/api/sections/${sectionId}/content/accept`, {
      content,
      messageId,
      changeSummary,
    });
  }

  // ==========================================
  // RFP RESEARCH API METHODS
  // ==========================================

  /**
   * Upload RFP file (single document or ZIP)
   */
  async uploadRFPFile(file, sourceUrl = null) {
    const formData = new FormData();
    formData.append('rfpFile', file);
    if (sourceUrl) {
      formData.append('sourceUrl', sourceUrl);
    }

    return this.client.post('/api/rfp-research/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  /**
   * Create research entry with selected documents
   */
  async createRFPResearch(selectedDocs, sourceUrl = null) {
    return this.post('/api/rfp-research/create', {
      selectedDocs,
      sourceUrl,
    });
  }

  /**
   * Start AI analysis of RFP
   */
  async analyzeRFP(researchId, contextDocumentIds, selectedModel = null) {
    return this.post(`/api/rfp-research/${researchId}/analyze`, {
      contextDocumentIds,
      selectedModel,
    });
  }

  /**
   * Extract requirements from RFP
   */
  async extractRFPRequirements(researchId) {
    return this.post(`/api/rfp-research/${researchId}/extract-requirements`);
  }

  /**
   * Extract deliverables from RFP
   */
  async extractRFPDeliverables(researchId) {
    return this.post(`/api/rfp-research/${researchId}/extract-deliverables`);
  }

  /**
   * Extract proposal submission requirements from RFP
   */
  async extractRFPProposalRequirements(researchId) {
    return this.post(`/api/rfp-research/${researchId}/extract-proposal-requirements`);
  }

  /**
   * Convert RFP research to proposal
   */
  async convertRFPToProposal(researchId, data) {
    return this.post(`/api/rfp-research/${researchId}/convert-to-proposal`, data);
  }

  /**
   * List all RFP research entries
   */
  async listRFPResearch(filters = {}) {
    return this.get('/api/rfp-research', { params: filters });
  }

  /**
   * Get specific research entry with full analysis
   */
  async getRFPResearch(researchId) {
    return this.get(`/api/rfp-research/${researchId}`);
  }

  /**
   * Get RFP chat message history
   */
  async getRFPChatMessages(researchId) {
    return this.get(`/api/rfp-research/${researchId}/chat/messages`);
  }

  /**
   * Send RFP chat message (streaming)
   */
  async sendRFPChatMessage(researchId, message, onChunk, onComplete, onError) {
    try {
      const token = this.getStoredToken();
      const response = await fetch(
        `${this.baseURL}/api/rfp-research/${researchId}/chat/message`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ message }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.slice(6));

            if (data.error) {
              onError?.(data.error);
              return;
            }

            if (data.done) {
              onComplete?.();
              return;
            }

            if (data.chunk) {
              onChunk?.(data.chunk);
            }
          }
        }
      }
    } catch (error) {
      onError?.(error.message);
      throw error;
    }
  }

  /**
   * Clear RFP chat history
   */
  async clearRFPChatHistory(researchId) {
    return this.delete(`/api/rfp-research/${researchId}/chat/session`);
  }

  /**
   * Update research status
   */
  async updateRFPResearchStatus(researchId, status) {
    return this.put(`/api/rfp-research/${researchId}/status`, { status });
  }

  /**
   * Delete research entry
   */
  async deleteRFPResearch(researchId) {
    return this.delete(`/api/rfp-research/${researchId}`);
  }

  /**
   * Download RFP research report as Word document
   */
  async downloadRFPResearchReport(researchIds) {
    try {
      // Use direct axios call to bypass response interceptor that transforms data
      const token = this.getStoredToken();
      const response = await axios.post(
        `${this.baseURL}/api/rfp-research/download-report`,
        { researchIds },
        {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `RFP_Research_Report_${new Date().toISOString().split('T')[0]}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  async downloadSingleRFPResearch(researchId) {
    try {
      // Use direct axios call to bypass response interceptor that transforms data
      const token = this.getStoredToken();
      const response = await axios.get(
        `${this.baseURL}/api/rfp-research/${researchId}/download`,
        {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `RFP_Analysis_${researchId}_${new Date().toISOString().split('T')[0]}.docx`;
      if (contentDisposition) {
        // Try to match filename with or without quotes
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }

  /**
   * Get active context documents for RFP analysis
   */
  async getActiveContextDocuments() {
    return this.get('/api/context-documents', { params: { active: true } });
  }

  /**
   * Download a context document
   */
  async downloadContextDocument(documentId) {
    try {
      const response = await this.client.get(`/api/context-documents/${documentId}/download`, {
        responseType: 'blob'
      });
      return response;
    } catch (error) {
      console.error('Download context document failed:', error);
      throw error;
    }
  }

  /**
   * Replace a context document with a new file
   */
  async replaceContextDocument(documentId, file) {
    const formData = new FormData();
    formData.append('document', file);

    try {
      const response = await this.client.put(`/api/context-documents/${documentId}/replace`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      console.error('Replace context document failed:', error);
      throw error;
    }
  }

  // ==========================================
  // AI INSTRUCTION TEMPLATES API METHODS
  // ==========================================

  /**
   * Get all AI instruction templates for current user
   */
  async getAIInstructionTemplates() {
    return this.get('/api/ai-instruction-templates');
  }

  /**
   * Get specific AI instruction template
   */
  async getAIInstructionTemplate(templateId) {
    return this.get(`/api/ai-instruction-templates/${templateId}`);
  }

  /**
   * Create new AI instruction template
   */
  async createAIInstructionTemplate(templateName, instructions) {
    return this.post('/api/ai-instruction-templates', {
      templateName,
      instructions
    });
  }

  /**
   * Update AI instruction template
   */
  async updateAIInstructionTemplate(templateId, templateName, instructions) {
    return this.put(`/api/ai-instruction-templates/${templateId}`, {
      templateName,
      instructions
    });
  }

  /**
   * Delete AI instruction template
   */
  async deleteAIInstructionTemplate(templateId) {
    return this.delete(`/api/ai-instruction-templates/${templateId}`);
  }

  /**
   * Set template as default for user
   */
  async setDefaultAIInstructionTemplate(templateId) {
    return this.post(`/api/ai-instruction-templates/${templateId}/set-default`);
  }

  /**
   * Get default template for current user
   */
  async getDefaultAIInstructionTemplate() {
    return this.get('/api/ai-instruction-templates/default/current');
  }

  /**
   * Get system default instructions
   */
  async getSystemDefaultInstructions() {
    return this.get('/api/ai-instruction-templates/system/default');
  }

  // ==========================================
  // COMPANY DOCUMENTS API METHODS
  // ==========================================

  /**
   * Get all company documents by type
   * @param {string} type - 'past_performance' or 'resume'
   * @param {string} searchTerm - Optional search term
   */
  async getCompanyDocuments(type, searchTerm = null) {
    const params = { type };
    if (searchTerm) {
      params.search = searchTerm;
    }
    return this.get('/api/company-documents', { params });
  }

  /**
   * Upload a company document
   * @param {File} file - File to upload
   * @param {string} documentType - 'past_performance' or 'resume'
   */
  async uploadCompanyDocument(file, documentType) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);

    return this.client.post('/api/company-documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  /**
   * View company document details and text
   * @param {number} documentId - Document ID
   */
  async viewCompanyDocument(documentId) {
    return this.get(`/api/company-documents/${documentId}/view`);
  }

  /**
   * Download company document
   * @param {number} documentId - Document ID
   */
  async downloadCompanyDocument(documentId) {
    const response = await this.client.get(
      `/api/company-documents/${documentId}/download`,
      { responseType: 'blob' }
    );
    return response;
  }

  /**
   * Delete company document
   * @param {number} documentId - Document ID
   */
  async deleteCompanyDocument(documentId) {
    return this.delete(`/api/company-documents/${documentId}`);
  }

  /**
   * Get multiple company documents by IDs (for AI context)
   * @param {Array<number>} documentIds - Array of document IDs
   */
  async getCompanyDocumentsByIds(documentIds) {
    return this.post('/api/company-documents/get-by-ids', { documentIds });
  }

  // Model Content Methods
  async getModelContentDocuments() {
    return this.get('/api/model-content');
  }

  async getModelContentDocument(documentId) {
    return this.get(`/api/model-content/${documentId}`);
  }

  async uploadModelContentDocument(file, documentName, description) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentName', documentName);
    if (description) {
      formData.append('description', description);
    }

    return this.client.post('/api/model-content', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async updateModelContentDocument(documentId, documentName, description) {
    return this.put(`/api/model-content/${documentId}`, {
      documentName,
      description
    });
  }

  async deleteModelContentDocument(documentId) {
    return this.delete(`/api/model-content/${documentId}`);
  }

  async downloadModelContentDocument(documentId, filename) {
    try {
      const response = await this.client.get(`/api/model-content/${documentId}/download`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  // Model Content Image Methods
  async getModelContentImages() {
    return this.get('/api/model-content/images');
  }

  async uploadModelContentImage(file, imageName, description) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('imageName', imageName);
    if (description) {
      formData.append('description', description);
    }

    return this.client.post('/api/model-content/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async updateModelContentImage(imageId, imageName, description) {
    return this.put(`/api/model-content/images/${imageId}`, {
      imageName,
      description
    });
  }

  async deleteModelContentImage(imageId) {
    return this.delete(`/api/model-content/images/${imageId}`);
  }

  getModelContentImageUrl(imageId) {
    const token = this.getStoredToken();
    return `${this.baseURL}/api/model-content/images/${imageId}/file?token=${token}`;
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient();
export default apiClient;
